/**
 * USB barcode scanners type like a keyboard and press Enter.
 *
 * That means the sale screen must accept a scan with no input focused, and must be able to tell
 * a scan from a person typing — otherwise every fast typist triggers a lookup, and every scan
 * into a focused search box gets treated as a search term and loses the Enter.
 *
 * The tell is rhythm. A scanner emits characters at a near-constant 5–20 ms; a human is slower
 * and irregular. So the buffer is kept, the intervals are measured, and the burst only counts as
 * a scan if it was fast, even, long enough, and terminated by Enter.
 */

import { useEffect, useRef } from 'react';

/** Above this median interval it is a person. 30 ms is comfortably above any real scanner. */
const MAX_MEDIAN_MS = 30;
/** A single stall this long ends the burst — the user paused, so it was typing. */
const MAX_GAP_MS = 120;
/** Shorter than this and a two-key shortcut would qualify. */
const MIN_LENGTH = 6;

export type BarcodeScannerOptions = {
  onScan: (code: string) => void;
  enabled?: boolean;
};

export function useBarcodeScanner({ onScan, enabled = true }: BarcodeScannerOptions): void {
  // The handler is read through a ref so re-registering the listener is not needed every time
  // the callback identity changes — which, with a cart in the closure, is every render.
  const handler = useRef(onScan);
  handler.current = onScan;

  // External system: raw keyboard events on the document. There is no React event for input that
  // arrives with nothing focused, which is exactly the case this exists for.
  useEffect(() => {
    if (!enabled) return;

    let buffer = '';
    let intervals: number[] = [];
    let lastAt = 0;
    let timer = 0;

    const reset = () => {
      buffer = '';
      intervals = [];
      lastAt = 0;
    };

    const onKeyDown = (event: KeyboardEvent) => {
      // A scanner never sends these. A person reaching for a shortcut does.
      if (event.ctrlKey || event.altKey || event.metaKey) {
        reset();
        return;
      }

      const now = performance.now();
      const gap = lastAt === 0 ? 0 : now - lastAt;

      if (gap > MAX_GAP_MS) reset();

      if (event.key === 'Enter') {
        const code = buffer;
        const median = medianOf(intervals);
        reset();

        if (code.length >= MIN_LENGTH && median <= MAX_MEDIAN_MS) {
          // Only now is the Enter consumed. A slow burst falls through, so pressing Enter in a
          // search box still submits the box.
          event.preventDefault();
          event.stopPropagation();
          handler.current(code);
        }

        return;
      }

      // Barcodes are printable and single-character. `Shift`, `Tab`, arrows and the rest are not
      // part of one and must not break a burst that is genuinely in progress.
      if (event.key.length !== 1) return;

      if (lastAt !== 0) intervals.push(gap);
      buffer += event.key;
      lastAt = now;

      // A burst that never gets its Enter is abandoned rather than left to contaminate the next
      // one. Without this, a partial scan plus later typing can concatenate into a false match.
      window.clearTimeout(timer);
      timer = window.setTimeout(reset, MAX_GAP_MS * 3);
    };

    // Capture phase: the event has to be seen before a focused input consumes it.
    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      window.clearTimeout(timer);
    };
  }, [enabled]);
}

/**
 * Median rather than mean: one stall — the OS scheduling the tab out mid-scan — would drag an
 * average past the threshold and lose a scan that was fine.
 */
function medianOf(values: readonly number[]): number {
  if (values.length === 0) return Number.POSITIVE_INFINITY;

  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);

  return sorted.length % 2 === 0
    ? ((sorted[middle - 1] ?? 0) + (sorted[middle] ?? 0)) / 2
    : (sorted[middle] ?? 0);
}
